import {
  MutationUpdateUserLocaleArgs,
  MutationDeleteOneTimeMessagesDismissedArgs,
  MutationPrepMemberForAtlassianAccountOnboardingArgs,
  MutationPrepMemberForEmailHygieneArgs,
  MutationAcceptDeveloperTermsArgs,
  MutationUpdateMemberAvatarSourceArgs,
  MutationUploadMemberAvatarArgs,
  MutationAddCampaignArgs,
  MutationUpdateCampaignArgs,
  MutationChangeMemberEmailArgs,
  MutationResendVerificationEmailArgs,
  QueryMemberCardsArgs,
} from '../generated';
import Cookie from 'js-cookie';
import {
  prepareDataForApolloCache,
  QueryInfo,
} from '../prepareDataForApolloCache';
import { JSONObject } from '../types';
import { token } from '@trello/session-cookie';
import { clientVersion } from '@trello/config';
import { parseNetworkError } from '@trello/graphql-error-handling';

export async function updateUserLocale(
  obj: object,
  args: MutationUpdateUserLocaleArgs,
  context: object,
  info: QueryInfo,
) {
  const searchParams = new URLSearchParams();

  searchParams.set('prefs/locale', args.locale);
  searchParams.set('token', token || '');

  const response = await fetch(`/1/members/${args.memberId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      'X-Trello-Client-Version': clientVersion,
    },
    body: searchParams,
  });

  const member = await response.json();

  return prepareDataForApolloCache(member, info);
}

export async function prepMemberForAtlassianAccountOnboarding(
  obj: object,
  args: MutationPrepMemberForAtlassianAccountOnboardingArgs,
  context: object,
  info: QueryInfo,
) {
  const searchParams = new URLSearchParams();

  searchParams.set('token', token || '');

  // Perform the PUT
  await fetch(`/1/member/${args.memberId}/logins/${args.aaLoginId}/primary`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      'X-Trello-Client-Version': clientVersion,
    },
    body: searchParams,
  });

  // Delete all the other logins
  for (const idLogin of args.nonAaLoginIds) {
    await fetch(`/1/member/${args.memberId}/logins/${idLogin}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'X-Trello-Client-Version': clientVersion,
      },
      body: searchParams.toString(),
    });
  }

  // Re-fetch the Member so that the return will correctly update the apollo cache
  const response = await fetch(`/1/member/${args.memberId}?logins=true`, {
    headers: {
      'X-Trello-Client-Version': clientVersion,
    },
  });
  const member = await response.json();

  return prepareDataForApolloCache(member, info);
}

export async function prepMemberForEmailHygiene(
  obj: object,
  args: MutationPrepMemberForEmailHygieneArgs,
  context: object,
  info: QueryInfo,
) {
  const searchParams = new URLSearchParams();

  searchParams.set('token', token || '');

  // Perform the PUT
  const setPrimaryResponse = await fetch(
    `/1/member/${args.memberId}/logins/${args.primaryLoginId}/primary`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'X-Trello-Client-Version': clientVersion,
      },
      body: searchParams.toString(),
    },
  );

  if (!setPrimaryResponse.ok) {
    throw new Error(
      `Invalid response status ${setPrimaryResponse.status} from PUT ${setPrimaryResponse.url}`,
    );
  }

  // Delete all the other logins
  for (const idLogin of args.removeLoginIds) {
    const removeEmailResponse = await fetch(
      `/1/member/${args.memberId}/logins/${idLogin}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          'X-Trello-Client-Version': clientVersion,
        },
        body: searchParams.toString(),
      },
    );

    if (!removeEmailResponse.ok) {
      throw new Error(
        `Invalid response status ${removeEmailResponse.status} from DELETE ${removeEmailResponse.url}`,
      );
    }
  }

  if (args.dismissMessage) {
    const dismissResponse = await fetch(
      `/1/member/${args.memberId}/oneTimeMessagesDismissed?value=${args.dismissMessage}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          'X-Trello-Client-Version': clientVersion,
        },
        body: searchParams.toString(),
      },
    );

    if (!dismissResponse.ok) {
      throw new Error(
        `Invalid response status ${dismissResponse.status} from POST ${dismissResponse.url}`,
      );
    }
  }

  // Re-fetch the Member so that the return will correctly update the apollo cache
  const response = await fetch(`/1/member/${args.memberId}?logins=true`, {
    credentials: 'include',
    headers: {
      'X-Trello-Client-Version': clientVersion,
    },
  });
  if (!response.ok) {
    throw new Error(
      `Invalid response status ${response.status} from GET ${response.url}`,
    );
  }
  const member = await response.json();

  return prepareDataForApolloCache(member, info);
}
// Internal endpoint for development and trelloinc members
export async function deleteOneTimeMessagesDismissed(
  obj: object,
  args: MutationDeleteOneTimeMessagesDismissedArgs,
  context: object,
  info: QueryInfo,
) {
  const searchParams = new URLSearchParams();

  searchParams.set('value', args.message);
  searchParams.set('token', token || '');

  const response = await fetch(
    `/1/members/${args.memberId}/oneTimeMessagesDismissed`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'X-Trello-Client-Version': clientVersion,
      },
      body: searchParams,
    },
  );

  const member = await response.json();

  return prepareDataForApolloCache(member, info);
}

export async function addOneTimeMessagesDismissed(
  obj: object,
  args: {
    memberId: string;
    messageId: string;
  },
  context: object,
  info: QueryInfo,
) {
  const searchParams = new URLSearchParams();

  searchParams.set('value', args.messageId);
  searchParams.set('token', token || '');

  const response = await fetch(
    `/1/members/${args.memberId}/oneTimeMessagesDismissed`,
    {
      method: 'POST',
      credentials: 'include',
      body: searchParams,
      headers: {
        'X-Trello-Client-Version': clientVersion,
      },
    },
  );

  const member = await response.json();

  return prepareDataForApolloCache(member, info);
}

export async function memberAgreementsResolver(
  member: {
    id: string;
  },
  args: object,
  context: object,
  info: QueryInfo,
) {
  let model = null;

  const apiUrl = `/1/members/${member.id}/agreements`;

  try {
    const response = await fetch(apiUrl, {
      credentials: 'include',
      headers: {
        'X-Trello-Client-Version': clientVersion,
      },
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if (response.status === 404) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model ? prepareDataForApolloCache(model, info, 'Member') : model;
  } catch (err) {
    console.error(err);
    return model;
  }
}

export async function memberAtlassianOrganizationsResolver(
  member: {
    id: string;
  },
  args: object,
  context: object,
  info: QueryInfo,
) {
  let model = null;

  const apiUrl = `/1/members/${member.id}/atlassianOrganizations`;

  try {
    const response = await fetch(apiUrl, {
      credentials: 'include',
      headers: {
        'X-Trello-Client-Version': clientVersion,
      },
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if (response.status === 404) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model ? prepareDataForApolloCache(model, info, 'Member') : model;
  } catch (err) {
    console.error(err);
    return model;
  }
}

export async function acceptDeveloperTerms(
  obj: object,
  args: MutationAcceptDeveloperTermsArgs,
  context: object,
  info: QueryInfo,
) {
  let model = null;

  const apiUrl = `/1/members/${args.memberId}/agreements`;

  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': clientVersion,
      },
      body: JSON.stringify({
        agreementType: 'developer-terms',
        token,
      }),
    });

    if (response.ok) {
      model = await response.json();
    } else {
      throw new Error(
        `An error occurred while resolving a GraphQL mutation. (status: ${response.status}, statusText: ${response.statusText})`,
      );
    }

    return model ? prepareDataForApolloCache(model, info) : model;
  } catch (err) {
    console.error(err);
    return model;
  }
}

export async function updateMemberAvatarSource(
  obj: object,
  args: MutationUpdateMemberAvatarSourceArgs,
  context: object,
  info: QueryInfo,
) {
  const body = new URLSearchParams();
  body.set('avatarSource', args.avatarSource);
  body.set('token', token || '');

  const response = await fetch(`/1/members/me`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      'X-Trello-Client-Version': clientVersion,
    },
    body,
  });

  const member = await response.json();

  return prepareDataForApolloCache(member, info);
}

export async function uploadMemberAvatar(
  obj: object,
  { file, uploadProgressCallback }: MutationUploadMemberAvatarArgs,
  context: object,
  info: QueryInfo,
) {
  const formData = new FormData();
  formData.set('file', file);
  formData.set('token', token || '');

  // We need to use XHR in order to track upload progress
  const request = new Promise<JSONObject>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.withCredentials = true;

    if (uploadProgressCallback) {
      const upload = xhr.upload;

      upload.addEventListener('progress', (e) => {
        // 64kb is the minimum threshold
        if (!e.lengthComputable || e.total < 1024 * 64) {
          return;
        }

        uploadProgressCallback(Math.round((100 * e.loaded) / e.total));
      });

      upload.addEventListener('load', () => {
        uploadProgressCallback(100);
      });
    }

    xhr.open('POST', `/1/member/me/avatar`);
    xhr.onload = () => {
      const response = JSON.parse(xhr.response);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(response);
      } else {
        reject(response);
      }
    };
    xhr.onerror = () => {
      reject({ status: xhr.status, statusText: xhr.statusText });
    };

    xhr.send(formData);
  });

  try {
    const member = await request;
    return prepareDataForApolloCache(member, info);
  } catch (e) {
    throw new Error(e.message);
  }
}

export async function addCampaign(
  obj: object,
  args: MutationAddCampaignArgs,
  context: object,
  info: QueryInfo,
) {
  const response = await fetch('/1/members/me/campaigns', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      ...(args || {}),
      token,
    }),
  });

  if (response.ok) {
    return prepareDataForApolloCache(await response.json(), info);
  }

  throw await parseNetworkError(response);
}

export async function updateCampaign(
  obj: object,
  args: MutationUpdateCampaignArgs,
  context: object,
  info: QueryInfo,
) {
  const { campaignId, ...rest } = args;
  const response = await fetch(`/1/members/me/campaigns/${campaignId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      ...(rest || {}),
      token,
    }),
  });

  if (!response.ok) {
    throw await parseNetworkError(response);
  }
}

export async function changeMemberEmail(
  obj: object,
  args: MutationChangeMemberEmailArgs,
  context: object,
  info: QueryInfo,
) {
  let model = null;
  let response = null;

  const apiUrl = '/1/members/me/logins';

  const { email, loginId, primary } = args;

  try {
    response = await fetch(`${apiUrl}/${loginId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': clientVersion,
      },
      body: JSON.stringify({
        email,
        primary,
        token,
      }),
    });

    if (response.ok) {
      model = await response.json();

      if (args.dismissMessage) {
        const dismissResponse = await fetch(
          `/1/member/me/oneTimeMessagesDismissed`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-Trello-Client-Version': clientVersion,
            },
            body: JSON.stringify({
              token,
              value: args.dismissMessage,
            }),
          },
        );

        if (!dismissResponse.ok) {
          throw new Error(
            `Invalid response status ${dismissResponse.status} from POST ${dismissResponse.url}`,
          );
        }
      }
    } else {
      throw new Error(
        `An error occurred while resolving a GraphQL mutation. (status: ${response.status}, statusText: ${response.statusText})`,
      );
    }

    return model ? prepareDataForApolloCache(model, info) : model;
  } catch (err) {
    if (response) {
      // Sometimes the server sends us just the string as the error, however
      // sometimes the server also sends a JSON error with the key 'message'
      // as the error
      let message = await response.text(); // Parse it as text
      let data = null;

      try {
        data = JSON.parse(message); // Try to parse it as json
        message = data.message;
        // eslint-disable-next-line no-empty
      } catch {}

      throw new Error(message);
    } else {
      throw new Error('Unknown error');
    }
  }
}

export async function resendVerificationEmail(
  obj: object,
  args: MutationResendVerificationEmailArgs,
  context: object,
  info: QueryInfo,
) {
  let model = null;
  let response = null;

  const apiUrl = '/resendValidate';

  const { email } = args;

  response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      email,
      token: Cookie.get(`token${window.location.port ? '3000' : ''}`) || '',
    }),
  });

  if (response.ok) {
    model = await response.json();

    if (model.badEmail) {
      throw new Error('bad email');
    } else if (model.alreadyConfirmed) {
      throw new Error('already confirmed');
    }
  } else {
    throw new Error(
      `An error occurred while resolving a GraphQL mutation. (status: ${response.status}, statusText: ${response.statusText})`,
    );
  }

  return model ? prepareDataForApolloCache(model, info) : model;
}

export async function memberAtlassianAccountsResolver(
  parent: object,
  args: object,
  context: object,
  info: QueryInfo,
) {
  const apiUrl = '/1/members/me/logins/atlassianAccounts';

  const response = await fetch(apiUrl, {
    credentials: 'include',
    headers: {
      'X-Trello-Client-Version': clientVersion,
    },
  });

  if (response.ok) {
    const model = await response.json();
    return prepareDataForApolloCache(model, info);
  } else {
    throw new Error(
      `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
    );
  }
}

// Very specific resolver to use on /members/id/cards
export const memberCardsResolver = async (
  obj: object,
  args: QueryMemberCardsArgs,
  context: object,
  info: QueryInfo,
) => {
  const { id, limit, before, modifiedSince, sort } = args;

  const apiUrl = `/1/members/${id}/cards`;

  const params = new URLSearchParams();
  params.set('filter', 'visible');
  params.set('stickers', 'true');
  params.set('attachments', 'true');
  params.set('members', 'true');

  if (limit) {
    params.set('limit', limit.toString());
  }
  if (before) {
    params.set('before', before);
  }
  if (modifiedSince) {
    params.set('modifiedSince', modifiedSince.toString());
  }
  if (sort) {
    params.set('sort', sort);
  }

  const response = await fetch(`${apiUrl}?${params}`, {
    credentials: 'include',
    headers: {
      'X-Trello-Client-Version': clientVersion,
    },
  });

  if (response.ok) {
    const model = await response.json();
    return prepareDataForApolloCache(model, info);
  } else {
    throw new Error(
      `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
    );
  }
};
